import React, { useContext } from 'react';
import { Lock } from 'lucide-react';
import { useP } from '../i18n/useP';
import { ReportingContext } from './ReportingControls';
import './PlanVersionPicker.css';

/* SÉLECTEUR DE VERSION DE PLAN (2026-09-27) — dans la bande de contexte, à côté
   de l'exercice : « quand je lis » vaut pour l'année comme pour la version.
   Il ne se montre que dans les modules qui planifient (Ventes & Ops, Finance),
   et lit la version courante du client tant qu'on n'en a pas choisi une autre.
   Une version verrouillée porte le cadenas : on la lit, on n'y écrit pas. Les
   versions se créent et se verrouillent dans Paramètres › Planification ; la
   dernière option de la liste y mène directement (demande d'Olivier,
   2026-10-03). */
const MANAGE = '__manage';
export default function PlanVersionPicker() {
  const p = useP();
  const ctx = useContext(ReportingContext);
  if (!ctx || !ctx.showVersion || !ctx.versions?.length) return null;
  const { versions, versionId, onVersionChange, onManageVersions } = ctx;
  const current = versions.find((v) => v.is_current) || versions[0];
  const selected = versions.find((v) => v.id === versionId) || current;
  /* LA VERSION SE VOIT (demande d'Olivier, 2026-09-28) : la pastille prend la
     couleur choisie pour la version dans Paramètres › Planification, l'accent du thème
     par défaut. On sait d'un regard sur quel plan on travaille. */
  const colorKey = selected?.color || 'slate';
  return (
    <div className="plan-version" title={p('Version de plan')}
         style={{ '--plan-c': `var(--color-plan-${colorKey})` }}>
      <span className="plan-version__dot" aria-hidden="true" />
      {/* LE NOM SE LIT DANS UN TEXTE, la liste native se pose par-dessus, invisible :
          une liste native prend la largeur de sa plus longue option (« Modifier /
          nouveau… »), ce qui décentrait le nom dans la pastille (demande d'Olivier,
          2026-10-12). Le cadenas suit le nom d'une version verrouillée. */}
      <span className="plan-version__label">{selected?.label}</span>
      {/* LA RÉPARTITION RÉALISÉ + PLANIFIÉ (demande d'Olivier, 2026-10-22) : « 9+3 », neuf mois lus du réel et
          trois planifiés ; une prévision glissante suit la clôture du client. Posée ici, elle se lit partout
          où le bouton du plan apparaît. */}
      {Number.isInteger(selected?.actual_months_now) && (
        <span className="plan-version__split"
              title={`${selected.actual_months_now} ${p('mois réalisés')}, ${12 - selected.actual_months_now} ${p('planifiés')}`}>
          · {selected.actual_months_now}+{12 - selected.actual_months_now}
        </span>
      )}
      {selected?.is_locked && <Lock size={14} className="plan-version__lock" aria-label={p('Verrouillée')} />}
      <select
        className="plan-version__select"
        aria-label={p('Version de plan')}
        value={selected?.id || ''}
        onChange={(e) => {
          if (e.target.value === MANAGE) { if (onManageVersions) onManageVersions(); return; }
          onVersionChange(e.target.value === current?.id ? null : e.target.value);
        }}
      >
        {versions.map((v) => (
          <option key={v.id} value={v.id}>
            {v.label}{v.is_locked ? ' 🔒' : ''}
          </option>
        ))}
        {onManageVersions && <option disabled>──────────</option>}
        {onManageVersions && <option value={MANAGE}>{p('Modifier / nouveau…')}</option>}
      </select>
    </div>
  );
}
